import type {Matrix4} from "@cm/math"
import type {IMeshGeometryQueries} from "#template/interfaces/scene-manager"
import type {SceneNodes} from "#template/interfaces/scene-object"
import type {InfoNodes} from "#template/interfaces/info-nodes"
import type {ObjectPath} from "#template/template-node/types"
import type {TemplateNode} from "#template/template-node/template-node"
import type {TemplateGraph} from "#template/nodes/template-graph/template-graph"

export type Intersection = {
    distance: number
    point: {x: number; y: number; z: number}
    faceIndex?: number | undefined | null
    uv?: {x: number; y: number} | undefined
    uv1?: {x: number; y: number} | undefined
    normal?: {x: number; y: number; z: number}
}

export enum TransformMode {
    Translate = "translate",
    Rotate = "rotate",
    Scale = "scale",
}

export type ControlPointPart = `controlPoint${number}`

export type TemplateNodePart = {
    templateNode: TemplateNode
    part: "root" | "target" | `group${number}` | ControlPointPart
}

export type TemplateNodePartIntersection = {templateNodePart: TemplateNodePart; intersection?: Intersection}

export const isGroupPart = (part: string): part is `group${number}` => part.match(/group\d+/) !== null
export const getGroupPartNumber = (part: `group${number}`): number => {
    const match = part.match(/group(\d+)$/)

    if (match && match.length > 1) {
        const slot = match[1]
        return parseInt(slot)
    }

    throw new Error(`Invalid group part: ${part}`)
}
export const getGroupPartForNumber = (groupNumber: number): `group${number}` => `group${groupNumber}`

export const isControlPointPart = (part: string): part is ControlPointPart => part.match(/controlPoint\d+/) !== null
export const getControlPointPartNumber = (part: ControlPointPart): number => {
    const match = part.match(/controlPoint(\d+)$/)

    if (match && match.length > 1) {
        const slot = match[1]
        return parseInt(slot)
    }

    throw new Error(`Invalid control point part: ${part}`)
}
export const getControlPointPartForNumber = (controlPointNumber: number): ControlPointPart => `controlPoint${controlPointNumber}`

export type SceneNodePart = {
    sceneNode: SceneNodes.SceneNode
    part: TemplateNodePart["part"]
}

export type InfoNodePart = {
    infoNode: InfoNodes.InfoNode
    part: TemplateNodePart["part"]
}

export type ObjectPathPart = {path: ObjectPath; part: TemplateNodePart["part"]}

export interface TransformAccessor {
    getTransform(): Matrix4 | undefined
    setTransform(matrix: Matrix4, interactive?: boolean): void
    endTransform(): void
    readonly controlledBySolver: boolean
    checkSolverNeedsUpdate(): boolean
}

export interface TransformInteraction {
    update(transform: Matrix4): void
    commit(): void
    cancel(): void
}

export interface LiveValueAccessor<T> {
    setLiveValue(value: T): void
    endInteraction(): void
}

export interface IModifyTemplateGraph {
    modifyTemplateGraph(modifier: (templateGraph: TemplateGraph) => void): void
}

export interface IGetInfoNodeParts {
    getInfoNodeParts(nodePart: TemplateNodePart): InfoNodePart[]
}

export interface INodeSceneInteractionsContext extends IModifyTemplateGraph, IGetInfoNodeParts {
    getTransformAccessor(templateNode: TemplateNode): TransformAccessor | undefined
    getLiveValueAccessor<T>(templateNode: TemplateNode): LiveValueAccessor<T> | undefined
    getSceneNodes(nodePart: TemplateNode): SceneNodes.SceneNode[]
    getSceneNodePartsUnderCursor(pointX: number, pointY: number): {sceneNodePart: SceneNodePart; intersection: Intersection}[]
    meshGeometryQueries: IMeshGeometryQueries
    canvasRect: {width: number; height: number}
    camera: {
        worldInverseMatrix: Matrix4
        projectionMatrix: Matrix4
    }
    transformMode: TransformMode
}

export interface IProceduralTemplateContext extends IGetInfoNodeParts {}
